/**
 * Web → mobile app handoff for incomplete onboarding (#1221).
 *
 * Consumer onboarding lives only in the mobile app. Web signup creates a user
 * with schema default `onboarding.currentStep = 'welcome'` and historically
 * dumped them into `/activity` — nowhere to go. Post-auth we route unfinished
 * accounts to the store/download surface instead.
 *
 * Completion predicate mirrors backend `isOnboardingComplete` plus skip
 * (pause still unlocks the product surfaces for returning users).
 */

export const WEB_SIGNUP_HANDOFF_PATH = '/download?source=web-signup';
export const DEFAULT_POST_AUTH_PATH = '/activity';

/** Product routes that require finished (or skipped) mobile onboarding. */
const PRODUCT_PATH_PREFIXES = [
  '/activity',
  '/money',
  '/budgets',
  '/goals',
  '/bills',
  '/discover',
  '/dashboard',
  '/subscription',
];

/**
 * @param {object|null|undefined} user — auth/verify user payload
 * @returns {boolean}
 */
export function hasFinishedMobileOnboarding(user) {
  if (!user || typeof user !== 'object') return false;

  const onboarding = user.onboarding || {};

  // Explicit finish / skip / progress-100.
  if (onboarding.completedAt) return true;
  if (onboarding.skippedAt) return true;
  if (onboarding.progress === 100) return true;

  // Family simplified flow (completedAt may be set separately; also check steps).
  const familyRole = user.family?.role;
  if (familyRole && familyRole !== 'primary' && familyRole !== 'owner') {
    const welcomeDone = !!onboarding.familyWelcomeCompleted;
    const profileDone =
      !!onboarding.familyProfileCompleted || !!(user.firstName && user.lastName);
    if (welcomeDone && profileDone) return true;
  }

  // Primary path — same shape as backend/services/onboardingCompletion.js
  const hasConfirmedAccounts = !!onboarding.hasConfirmedAllAccounts;
  const isManualPath = !hasConfirmedAccounts;
  if (
    onboarding.initialBudgetComplete &&
    onboarding.initialBudgetReviewedAndApproved &&
    (isManualPath || onboarding.initialCategorizationComplete)
  ) {
    return true;
  }

  return false;
}

/**
 * A signed-out visitor who opens a private link (e.g. the reconnect email's
 * /plaid-link?plaidItemId=…) is bounced to the landing page to sign in. The
 * landing page clears router state when it opens the login modal, so the
 * intended destination is kept in sessionStorage and consumed after auth.
 */
export const POST_AUTH_RETURN_KEY = 'budgit.postAuthReturnTo';

/** Same-origin app path only: no protocol-relative or backslash tricks. */
function isSafeReturnPath(path) {
  return (
    typeof path === 'string' &&
    path.startsWith('/') &&
    !path.startsWith('//') &&
    !path.includes('\\') &&
    path !== '/'
  );
}

/** @param {{ pathname?: string, search?: string }|null|undefined} location */
export function rememberPostAuthReturn(location) {
  const path = `${location?.pathname || ''}${location?.search || ''}`;
  if (!isSafeReturnPath(path)) return;
  try {
    window.sessionStorage.setItem(POST_AUTH_RETURN_KEY, path);
  } catch (_) {
    // Storage unavailable (private mode) — the user lands on the default page.
  }
}

/** Paths a /login?returnTo= value may name. Not a decode-depth check. */
const POST_AUTH_RETURN_ALLOWLIST = new Set(['/account']);

/**
 * `/login?returnTo=` may only be `/account`, plus an optional query whose
 * keys and values are a plain token alphabet. Percent-encoding, schemes,
 * and protocol-relative values are rejected without being decoded into a path.
 * @param {string|null|undefined} raw
 * @returns {string|null}
 */
export function allowlistedPostAuthReturn(raw) {
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (!trimmed.startsWith('/') || trimmed.startsWith('//')) return null;
  if (trimmed.includes('%') || trimmed.includes('\\') || trimmed.includes('//') || trimmed.includes(':') || trimmed.includes('#')) {
    return null;
  }
  if (/[\u0000-\u001f\s]/.test(trimmed)) return null;
  const queryAt = trimmed.indexOf('?');
  const pathname = queryAt === -1 ? trimmed : trimmed.slice(0, queryAt);
  const search = queryAt === -1 ? '' : trimmed.slice(queryAt);
  if (!POST_AUTH_RETURN_ALLOWLIST.has(pathname)) return null;
  if (search) {
    const params = new URLSearchParams(search);
    for (const [key, value] of params) {
      if (!/^[A-Za-z0-9_-]{1,64}$/.test(key)) return null;
      if (!/^[A-Za-z0-9._~-]{0,200}$/.test(value)) return null;
    }
  }
  return pathname + search;
}

/**
 * `/login?returnTo=` from the trial-ended email.
 * @param {string} search
 * @returns {string|null}
 */
export function clearPostAuthReturn() {
  try {
    window.sessionStorage.removeItem(POST_AUTH_RETURN_KEY);
  } catch (_) {
    // Storage unavailable.
  }
}

export function rememberReturnToParam(search) {
  const rawSearch = typeof search === 'string' ? search : '';
  const params = new URLSearchParams(
    rawSearch.startsWith('?') ? rawSearch.slice(1) : rawSearch
  );
  if (!params.has('returnTo')) return null;
  const path = allowlistedPostAuthReturn(params.get('returnTo'));
  if (!path) return null;
  try {
    window.sessionStorage.setItem(POST_AUTH_RETURN_KEY, path);
  } catch (_) {
    return null;
  }
  return path;
}

/**
 * A path this app may navigate to. Decodes until the text stops changing,
 * then keeps only a single-slash relative path. Used by the checkout-cancel
 * "return" query, which must not assign `//host`.
 * @param {string|null|undefined} raw
 * @returns {string|null}
 */
export function safeSameOriginReturn(raw) {
  if (typeof raw !== 'string') return null;
  let current = raw.trim();
  if (!current) return null;
  let stable = false;
  for (let i = 0; i < 8; i += 1) {
    if (/[\u0000-\u001f\s]/.test(current)) return null;
    let next;
    try {
      next = decodeURIComponent(current);
    } catch (_) {
      return null;
    }
    if (next === current) {
      stable = true;
      break;
    }
    current = next;
  }
  if (!stable) return null;
  if (!current.startsWith('/') || current.startsWith('//')) return null;
  if (current.includes('\\') || current.includes('//') || current.includes(':')) return null;
  if (/[\u0000-\u001f\s]/.test(current)) return null;
  return current;
}

function isAccountSubscribeReturn(path) {
  if (!path) return false;
  const pathname = String(path).split('?')[0].split('#')[0];
  return pathname === '/account';
}

function takePostAuthReturn() {
  try {
    const path = window.sessionStorage.getItem(POST_AUTH_RETURN_KEY);
    window.sessionStorage.removeItem(POST_AUTH_RETURN_KEY);
    return isSafeReturnPath(path) ? path : null;
  } catch (_) {
    return null;
  }
}

/**
 * Where to send the browser after a successful web auth. A saved private link
 * wins over the default. Unfinished accounts go to the app handoff, except a
 * saved /account path from the trial-ended email.
 * @param {object|null|undefined} user
 * @returns {string} react-router path (may include query)
 */
export function postAuthDestination(user) {
  const returnTo = takePostAuthReturn();
  if (!hasFinishedMobileOnboarding(user)) {
    // /account is outside the product handoff list, so an expired trial can
    // subscribe there. Other saved paths still go to the app download handoff.
    if (isAccountSubscribeReturn(returnTo)) return returnTo;
    return WEB_SIGNUP_HANDOFF_PATH;
  }
  return returnTo || DEFAULT_POST_AUTH_PATH;
}

/**
 * True when a private product route should bounce incomplete users to the app handoff.
 * @param {string} pathname
 * @param {object|null|undefined} user
 */
export function shouldHandoffIncompleteOnboarding(pathname, user) {
  if (hasFinishedMobileOnboarding(user)) return false;
  if (!pathname || typeof pathname !== 'string') return false;
  const path = pathname.split('?')[0] || '';
  return PRODUCT_PATH_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`)
  );
}
